// MR-MOB — **`--nav-h` IS A STATIC 88px AND THE BAR IS NOT ALWAYS 88px.**
//
// Found by this round's own walk, which is the argument for measuring at coordinates: MR-MOB moved
// the Magic Room's two edge controls to `calc(var(--nav-h) + 10px)` so they would clear the header,
// and at 393x852 they did. **At 360x740 they still sat on it** — the nav WRAPS to two rows at that
// width (W1-4's own comment records the wrap at 390px), so the real bar runs `0..132` while the
// token says 88. A constant that names a measured thing rots the moment the thing changes, which is
// the defect this whole round is about, one layer further down.
//
// **THE FIX IS SCOPED ON PURPOSE, AND THAT IS A DELIBERATE NON-CHANGE (R25/R19).** Thirteen other
// rules read `--nav-h` — drawer tops, panel max-heights, page padding — and re-pointing them at a
// measured value would move a dozen surfaces in a round that was asked to fix a dropdown and two
// buttons. So this publishes a SECOND variable and changes nothing that reads the first one. What
// consumes it today is `.mr-edge-controls` and nothing else.
//
// **THE FALLBACK IS THE STATIC TOKEN, SO THE FAILURE MODE IS TODAY'S BEHAVIOUR** (R163's ruling in
// another spelling: a mechanism whose failure mode is the status quo and never something worse).
// Every consumer writes `var(--nav-real-h, var(--nav-h))`, so a browser without `ResizeObserver`, a
// nav that has not mounted, or this component being removed all land exactly where the site is now.
import { useEffect } from "react";

export const NAV_HEIGHT_VAR = "--nav-real-h";

// PURE, so a gate can run it: what to write for a measured height, and what to write when there is
// nothing to measure. `null` means "publish nothing and let the fallback stand" — deliberately not
// `0`, which would collapse every consumer against the top of the screen (R18: an absent
// measurement and a measurement of zero are different facts).
export function navHeightValue(height) {
  if (!Number.isFinite(height) || height <= 0) return null;
  return `${Math.round(height)}px`;
}

/** Renders nothing. Publishes the bar's REAL height on the root element and keeps it current. */
export function NavHeightVar() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const sync = () => {
      const nav = document.querySelector(".nav");
      const value = navHeightValue(nav ? nav.getBoundingClientRect().height : NaN);
      if (value) root.style.setProperty(NAV_HEIGHT_VAR, value);
      else root.style.removeProperty(NAV_HEIGHT_VAR);
    };
    sync();
    // A ResizeObserver, because the bar changes height by WRAPPING rather than by the window
    // changing size — a `resize` listener alone misses a row appearing when a language switch or a
    // longer pill label pushes the group onto a second line.
    let ro = null;
    const nav = document.querySelector(".nav");
    if (nav && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(sync);
      ro.observe(nav);
    }
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("resize", sync);
      if (ro) ro.disconnect();
      root.style.removeProperty(NAV_HEIGHT_VAR);
    };
  }, []);
  return null;
}
